import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions,
  CardHeader,
  Button,
  IconButton,
  Skeleton,
  Alert,
  Snackbar,
  Divider,
  Tabs,
  Tab,
  Paper,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';

// Icons
import DeleteIcon from '@mui/icons-material/Delete';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Components
import EnhancedChart from '../components/common/EnhancedChart';
import ChartContainer from '../components/common/ChartContainer';
import { chartColors } from '../utils/colorUtils';

const Dashboard = () => {
  const theme = useTheme();
  const [years, setYears] = useState([]);
  const [yearsReady, setYearsReady] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [pinnedCharts, setPinnedCharts] = useState([]);
  const [evTopSellers, setEvTopSellers] = useState(null);
  const [iceTopSellers, setIceTopSellers] = useState(null);
  /** Brand rankings from /api/sales/* — same source as EV/ICE Analytics pages (reliable top 10). */
  const [evSalesRank, setEvSalesRank] = useState([]);
  const [iceSalesRank, setIceSalesRank] = useState([]);
  const [marketTrends, setMarketTrends] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [topStates, setTopStates] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [topRtos, setTopRtos] = useState([]);
  const [loading, setLoading] = useState({
    pinnedCharts: true,
    insights: true
  });
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [excelDownloadLoading, setExcelDownloadLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState({ ev: '—', ice: '—' });
  
  // Chart refs for the predefined analytics charts
  const evChartRef = useRef(null);
  const iceChartRef = useRef(null);
  const trendsChartRef = useRef(null);
  const comparisonChartRef = useRef(null);
  const topStatesRef = useRef(null);
  const topCitiesRef = useRef(null);
  const topRtosRef = useRef(null);
  
  useEffect(() => {
    fetchPinnedCharts();
    fetchYears();
  }, []);

  useEffect(() => {
    if (!yearsReady) return;
    if (years.length > 0 && !selectedYear) return;
    fetchDashboardData();
    fetchExcelLastUpdated();
  }, [selectedYear, yearsReady, years.length]);

  const fetchYears = async () => {
    try {
      const res = await axios.get('/api/years');
      const y = Array.isArray(res.data) ? res.data : [];
      setYears(y);
      // Default to latest year if available (e.g. 2025) so all API calls use ?year= from DB
      if (y.length && selectedYear === '') {
        const latest = [...y].sort((a, b) => Number(a) - Number(b)).at(-1);
        if (latest !== undefined && latest !== null) setSelectedYear(String(latest));
      }
    } catch (err) {
      console.error('Error fetching years:', err);
    } finally {
      setYearsReady(true);
    }
  };
  
  // Filename format: vahan_master_sheet_<EV_DATE>,<ICE_DATE>.xlsx (e.g. ..._25-05-26,22-05-26.xlsx)
  // First DD-MM-YY = EV, second = ICE. Falls back gracefully if only one date is present.
  const extractDateFromExcelName = (fileName) => {
    if (typeof fileName !== 'string') return { ev: null, ice: null };
    const matches = fileName.match(/\d{2}-\d{2}-\d{2}/g) || [];
    return { ev: matches[0] || null, ice: matches[1] || null };
  };

  const fetchExcelLastUpdated = async () => {
    try {
      const params = selectedYear ? { year: selectedYear } : {};
      const { data } = await axios.get('/api/dashboard/excel-files', { params });
      const files = Array.isArray(data?.files) ? data.files : [];
      const first = files[0];
      const parsed = extractDateFromExcelName(first?.name);
      setLastUpdated({ ev: parsed.ev || '—', ice: parsed.ice || '—' });
    } catch (err) {
      console.error('Excel last updated:', err);
      setLastUpdated({ ev: '—', ice: '—' });
    }
  };

  const handleDownloadMasterExcel = async () => {
    setExcelDownloadLoading(true);
    try {
      const params = selectedYear ? { year: selectedYear } : {};
      const { data } = await axios.get('/api/dashboard/excel-files', { params });
      const files = Array.isArray(data?.files) ? data.files : [];
      const first = files[0];
      const url = first?.url;
      if (!url || typeof url !== 'string') {
        setSnackbar({
          open: true,
          message: data?.message || 'No Excel file URL available. Configure the backend or try again later.',
          severity: 'warning',
        });
        return;
      }
      const name = first?.name && typeof first.name === 'string' ? first.name : 'vahan-master.xlsx';
      const parsed = extractDateFromExcelName(name);
      if (parsed.ev || parsed.ice) {
        setLastUpdated({ ev: parsed.ev || '—', ice: parsed.ice || '—' });
      }
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setSnackbar({ open: true, message: 'Opening Excel download…', severity: 'success' });
    } catch (err) {
      console.error('Excel download:', err);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 503
          ? 'Excel download is not configured on the server (EXCEL_FILES_API_URL).'
          : 'Could not download the Excel file.');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setExcelDownloadLoading(false);
    }
  };

  const fetchPinnedCharts = async () => {
    try {
      setLoading(prev => ({ ...prev, pinnedCharts: true }));
      const response = await axios.get('/api/dashboard/pinned');
      setPinnedCharts(response.data);
    } catch (err) {
      console.error('Error fetching pinned charts:', err);
      setError('Failed to load pinned charts');
    } finally {
      setLoading(prev => ({ ...prev, pinnedCharts: false }));
    }
  };
  
  const fetchDashboardData = async () => {
    try {
      setLoading(prev => ({ ...prev, insights: true }));
      const params = selectedYear ? { year: selectedYear } : undefined;

      const [
        evAnalyticsRes,
        iceAnalyticsRes,
        evSalesRes,
        iceSalesRes,
        trendsRes,
        evMonthlyRes,
        iceMonthlyRes,
        topStatesRes,
        topCitiesRes,
        topRtosRes
      ] = await Promise.all([
        axios.get('/api/vehicles/ev-analytics', { params }),
        axios.get('/api/vehicles/ice-analytics', { params }),
        axios.get('/api/sales/ev', { params }),
        axios.get('/api/sales/ice', { params }),
        axios.get('/api/vehicles/market-trends', { params }),
        axios.get('/api/sales/monthly', { params }),
        axios.get('/api/sales/monthly', { params: { ...params, type: 'ice' } }),
        axios.get('/api/vehicles/top-states', { params }),
        axios.get('/api/vehicles/top-cities', { params }),
        axios.get('/api/vehicles/top-rtos', { params })
      ]);

      setEvTopSellers(evAnalyticsRes.data);
      setIceTopSellers(iceAnalyticsRes.data);
      setEvSalesRank(Array.isArray(evSalesRes.data) ? evSalesRes.data : []);
      setIceSalesRank(Array.isArray(iceSalesRes.data) ? iceSalesRes.data : []);
      setMarketTrends(trendsRes.data || generateFallbackMarketTrends());
      setMonthlyTrends({
        ev: Array.isArray(evMonthlyRes.data) ? evMonthlyRes.data : [],
        ice: Array.isArray(iceMonthlyRes.data) ? iceMonthlyRes.data : []
      });
      setTopStates(Array.isArray(topStatesRes.data) ? topStatesRes.data : []);
      setTopCities(Array.isArray(topCitiesRes.data) ? topCitiesRes.data : []);
      setTopRtos(Array.isArray(topRtosRes.data) ? topRtosRes.data : []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      const evFb = generateFallbackEvData();
      const iceFb = generateFallbackIceData();
      setEvTopSellers(evFb);
      setIceTopSellers(iceFb);
      setEvSalesRank(evFb.topSellers.map((t) => ({ _id: t.maker, totalSales: t.sales, maker: t.maker })));
      setIceSalesRank(iceFb.topSellers.map((t) => ({ _id: t.maker, totalSales: t.sales, maker: t.maker })));
      setMarketTrends(generateFallbackMarketTrends());
      setMonthlyTrends(null);
      setTopStates([]);
      setTopCities([]);
      setTopRtos([]);
    } finally {
      setLoading(prev => ({ ...prev, insights: false }));
    }
  };
  
  // Generate fallback data if API fails
  const generateFallbackEvData = () => {
    return {
      topSellers: [
        { maker: 'Tesla', model: 'Model Y', sales: 8700 },
        { maker: 'Tesla', model: 'Model 3', sales: 7200 },
        { maker: 'BYD', model: 'Atto 3', sales: 5400 },
        { maker: 'MG', model: 'ZS EV', sales: 4800 },
        { maker: 'Tata', model: 'Nexon EV', sales: 4200 },
        { maker: 'Tata', model: 'Tiago EV', sales: 3900 },
        { maker: 'Hyundai', model: 'Kona Electric', sales: 3500 },
        { maker: 'Kia', model: 'EV6', sales: 3100 },
        { maker: 'Citroen', model: 'eC3', sales: 2800 },
        { maker: 'Mahindra', model: 'XUV400', sales: 2500 }
      ],
      monthlyData: [
        { month: 'Jan', sales: 14500 },
        { month: 'Feb', sales: 15200 },
        { month: 'Mar', sales: 16800 },
        { month: 'Apr', sales: 18400 },
        { month: 'May', sales: 19700 }
      ],
      totalSales: 84600
    };
  };
  
  const generateFallbackIceData = () => {
    return {
      topSellers: [
        { maker: 'Maruti Suzuki', model: 'Swift', sales: 15400 },
        { maker: 'Hyundai', model: 'Creta', sales: 14200 },
        { maker: 'Tata', model: 'Nexon', sales: 13800 },
        { maker: 'Maruti Suzuki', model: 'Baleno', sales: 12600 },
        { maker: 'Mahindra', model: 'Bolero', sales: 11200 },
        { maker: 'Toyota', model: 'Innova', sales: 10800 },
        { maker: 'Honda', model: 'City', sales: 9800 },
        { maker: 'Kia', model: 'Seltos', sales: 9400 },
        { maker: 'Renault', model: 'Kwid', sales: 8900 },
        { maker: 'Maruti Suzuki', model: 'Wagon R', sales: 8500 }
      ],
      monthlyData: [
        { month: 'Jan', sales: 112000 },
        { month: 'Feb', sales: 108000 },
        { month: 'Mar', sales: 105000 },
        { month: 'Apr', sales: 102000 },
        { month: 'May', sales: 99000 }
      ],
      totalSales: 526000
    };
  };
  
  const generateFallbackMarketTrends = () => {
    return {
      evShare: [
        { month: 'Jan', share: 4.2 },
        { month: 'Feb', share: 4.5 },
        { month: 'Mar', share: 4.8 },
        { month: 'Apr', share: 5.2 },
        { month: 'May', share: 5.6 }
      ],
      iceShare: [
        { month: 'Jan', share: 95.8 },
        { month: 'Feb', share: 95.5 },
        { month: 'Mar', share: 95.2 },
        { month: 'Apr', share: 94.8 },
        { month: 'May', share: 94.4 }
      ],
      comparisonData: [
        { year: '2019', ev: 2.1, ice: 97.9 },
        { year: '2020', ev: 2.8, ice: 97.2 },
        { year: '2021', ev: 3.5, ice: 96.5 },
        { year: '2022', ev: 4.3, ice: 95.7 },
        { year: '2023', ev: 5.6, ice: 94.4 }
      ],
      marketGrowthYoY: null,
      marketGrowthCompareYear: null,
      evMarketShareAnnual: null
    };
  };
  
  const handleDeleteChart = async (id) => {
    try {
      await axios.delete(`/api/dashboard/pin/${id}`);
      setPinnedCharts(prevCharts => prevCharts.filter(chart => chart._id !== id));
      setSnackbar({
        open: true,
        message: 'Chart removed from dashboard',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing chart:', err);
      setSnackbar({
        open: true,
        message: 'Failed to remove chart',
        severity: 'error'
      });
    }
  };
  
  const handleDownloadChart = (chart) => {
    // Create a temporary link element to download the image
    const link = document.createElement('a');
    link.href = chart.imageUrl;
    link.download = `${chart.title.replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSnackbar({
      open: true,
      message: 'Chart downloaded',
      severity: 'success'
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /** Shorten axis text in narrow columns; full name in tooltip via dataset.fullLabels */
  const shortenMakerName = (name, max = 26) => {
    const s = String(name || '');
    if (s.length <= max) return s;
    return `${s.slice(0, max - 1)}…`;
  };

  const topSalesBarOptions = {
    datasets: {
      bar: {
        borderRadius: 4,
        borderSkipped: false,
        categoryPercentage: 0.78,
        barPercentage: 0.88
      }
    },
    layout: {
      padding: { left: 4, right: 8, top: 2, bottom: 4 }
    }
  };

  // Charts use /api/sales/ev|ice (same as Analytics pages) so all 10 ranks show when data exists
  const getEvTopSellersChartData = () => {
    const fromApi = (evSalesRank || []).slice(0, 10);
    const rows =
      fromApi.length > 0
        ? fromApi
        : (evTopSellers?.topSellers || []).slice(0, 10).map((t) => ({
            _id: t.maker,
            totalSales: t.sales
          }));
    if (!rows.length) return null;

    const fullNames = rows.map((r) => r._id || r.maker || '');
    return {
      labels: fullNames.map((n) => shortenMakerName(n)),
      datasets: [
        {
          label: 'Sales',
          data: rows.map((r) => r.totalSales),
          fullLabels: fullNames,
          backgroundColor: rows.map((_, i) => alpha(chartColors[i % chartColors.length], 0.82)),
          borderColor: rows.map((_, i) => chartColors[i % chartColors.length]),
          borderWidth: 1
        }
      ]
    };
  };

  const getIceTopSellersChartData = () => {
    const fromApi = (iceSalesRank || []).slice(0, 10);
    const rows =
      fromApi.length > 0
        ? fromApi
        : (iceTopSellers?.topSellers || []).slice(0, 10).map((t) => ({
            _id: t.maker,
            totalSales: t.sales
          }));
    if (!rows.length) return null;

    const fullNames = rows.map((r) => r._id || r.maker || '');
    return {
      labels: fullNames.map((n) => shortenMakerName(n)),
      datasets: [
        {
          label: 'Sales',
          data: rows.map((r) => r.totalSales),
          fullLabels: fullNames,
          backgroundColor: rows.map((_, i) => alpha(chartColors[(i + 3) % chartColors.length], 0.82)),
          borderColor: rows.map((_, i) => chartColors[(i + 3) % chartColors.length]),
          borderWidth: 1
        }
      ]
    };
  };
  
  // Two lines: EV % and ICE % only. Tooltip also shows sales for that month so volume (e.g. 935) is clear.
  const getMarketTrendsChartData = () => {
    if (!marketTrends) return null;

    const labels = monthlyTrends?.ev?.length
      ? monthlyTrends.ev.map((d) => d.month)
      : marketTrends.evShare.map((item) => item.month);

    return {
      labels,
      datasets: [
        {
          label: 'EV Market Share (%)',
          data: marketTrends.evShare.map((item) => item.share),
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          fill: true,
          tension: 0.4,
          pointRadius: 3,
        },
        {
          label: 'ICE Market Share (%)',
          data: marketTrends.iceShare.map((item) => item.share),
          borderColor: theme.palette.secondary.main,
          backgroundColor: alpha(theme.palette.secondary.main, 0.12),
          fill: true,
          tension: 0.4,
          pointRadius: 3,
        },
      ],
    };
  };

  const marketTrendsChartOptions = marketTrends
    ? {
        scales: {
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: 'Market share (%)',
              color: theme.palette.text.secondary,
              font: { size: 11 },
            },
            grid: {
              color: alpha(theme.palette.divider, 0.1),
            },
            ticks: {
              color: theme.palette.text.secondary,
              callback: (v) => `${v}%`,
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                const idx = context.dataIndex;
                const share = context.parsed.y;
                if (share === null || share === undefined) return '';
                const dsLabel = context.dataset.label || '';
                const evSales = monthlyTrends?.ev?.[idx]?.sales;
                const iceSales = monthlyTrends?.ice?.[idx]?.sales;
                const pct = `${Number(share).toFixed(1)}%`;
                if (dsLabel.includes('EV')) {
                  const vol =
                    evSales != null && evSales !== ''
                      ? ` · EV sales: ${Number(evSales).toLocaleString()}`
                      : '';
                  return `EV market share: ${pct}${vol}`;
                }
                if (dsLabel.includes('ICE')) {
                  const vol =
                    iceSales != null && iceSales !== ''
                      ? ` · ICE sales: ${Number(iceSales).toLocaleString()}`
                      : '';
                  return `ICE market share: ${pct}${vol}`;
                }
                return `${dsLabel}: ${pct}`;
              },
            },
          },
        },
      }
    : undefined;
  
  // Generate chart data for EV vs ICE comparison
  const getComparisonChartData = () => {
    if (!marketTrends?.comparisonData) return null;
    
    return {
      labels: marketTrends.comparisonData.map(item => item.year),
      datasets: [
        {
          label: 'EV Share (%)',
          data: marketTrends.comparisonData.map(item => item.ev),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.dark,
          stack: 'Stack 0',
        },
        {
          label: 'ICE Share (%)',
          data: marketTrends.comparisonData.map(item => item.ice),
          backgroundColor: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.dark,
          stack: 'Stack 0',
        }
      ]
    };
  };
  
  // Statistical summary cards
  const renderStatCards = () => {
    const evTotal = Number(evTopSellers?.totalSales ?? NaN);
    const iceTotal = Number(iceTopSellers?.totalSales ?? NaN);
    const totalAll = (Number.isFinite(evTotal) ? evTotal : 0) + (Number.isFinite(iceTotal) ? iceTotal : 0);
    const evPen =
      totalAll > 0 && Number.isFinite(evTotal) ? (evTotal / totalAll) * 100 : null;
    const icePen =
      totalAll > 0 && Number.isFinite(iceTotal) ? (iceTotal / totalAll) * 100 : null;

    const statItems = [
      {
        title: 'Total EV Sales',
        value: evTopSellers?.totalSales?.toLocaleString() || '84,600',
        color: theme.palette.primary.main,
        icon: null,
        subValue: evPen != null ? `${evPen.toFixed(1)}%` : '—',
      },
      {
        title: 'Total ICE Sales',
        value: iceTopSellers?.totalSales?.toLocaleString() || '526,000',
        color: theme.palette.secondary.main,
        icon: null,
        subValue: icePen != null ? `${icePen.toFixed(1)}%` : '—',
      },
      {
        title: 'YoY Growth',
        value:
          marketTrends?.marketGrowthYoY != null && !Number.isNaN(marketTrends.marketGrowthYoY)
            ? `${marketTrends.marketGrowthYoY.toFixed(1)}%`
            : '—',
        color: theme.palette.info.main,
        icon: <SpeedIcon />,
      },
    ];
    
    const mdCols = statItems.length === 3 ? 4 : 3; // 12-grid: 3 cards => 4 each, 4 cards => 3 each

    return (
      <Grid container spacing={2}>
        {statItems.map((stat, index) => (
          <Grid item xs={12} sm={6} md={mdCols} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                border: `1px solid ${alpha(stat.color, 0.2)}`,
                boxShadow: `0 4px 12px ${alpha(stat.color, 0.08)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(stat.color, 0.12)}`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 2, pb: '16px !important', minHeight: 96, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {stat.title}
                  </Typography>
                  {stat.icon && (
                    <Box 
                      sx={{ 
                        p: 1, 
                        borderRadius: '50%', 
                        display: 'flex',
                        bgcolor: alpha(stat.color, 0.1),
                        color: stat.color
                      }}
                    >
                      {stat.icon}
                    </Box>
                  )}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25, lineHeight: 1.15 }}>
                  {stat.value}
                </Typography>
                {stat.subValue != null && (
                  <Typography variant="caption" sx={{ color: stat.color, fontWeight: 800 }}>
                    {stat.subValue}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const getTopStatesChartData = () => {
    const rows = topStates.slice(0, 10);
    if (!rows.length) return null;
    const fullNames = rows.map(r => r._id || r.state || '');
    return {
      labels: fullNames.map(n => shortenMakerName(n, 22)),
      datasets: [{
        label: 'EV Sales',
        data: rows.map(r => r.totalSales),
        fullLabels: fullNames,
        backgroundColor: rows.map((_, i) => alpha(chartColors[i % chartColors.length], 0.82)),
        borderColor: rows.map((_, i) => chartColors[i % chartColors.length]),
        borderWidth: 1
      }]
    };
  };

  const getTopCitiesChartData = () => {
    const rows = topCities.slice(0, 10);
    if (!rows.length) return null;
    const fullNames = rows.map(r => r._id || r.city || '');
    return {
      labels: fullNames.map(n => shortenMakerName(n, 22)),
      datasets: [{
        label: 'EV Sales',
        data: rows.map(r => r.totalSales),
        fullLabels: fullNames,
        backgroundColor: rows.map((_, i) => alpha(chartColors[(i + 2) % chartColors.length], 0.82)),
        borderColor: rows.map((_, i) => chartColors[(i + 2) % chartColors.length]),
        borderWidth: 1
      }]
    };
  };

  const formatRtoWithCode = (r) => {
    const name = String(r._id ?? r.rto ?? '').trim();
    const code = r.rtoCode != null && String(r.rtoCode).trim() !== ''
      ? String(r.rtoCode).trim()
      : '';
    if (name && code) return `${name} (${code})`;
    return name || code || '—';
  };

  const getTopRtosChartData = () => {
    const rows = topRtos.slice(0, 15);
    if (!rows.length) return null;
    const fullNames = rows.map(formatRtoWithCode);
    return {
      labels: fullNames.map((n) => shortenMakerName(n, 36)),
      datasets: [{
        label: 'EV Sales',
        data: rows.map(r => r.totalSales),
        fullLabels: fullNames,
        backgroundColor: rows.map((_, i) => alpha(chartColors[(i + 5) % chartColors.length], 0.82)),
        borderColor: rows.map((_, i) => chartColors[(i + 5) % chartColors.length]),
        borderWidth: 1
      }]
    };
  };

  const evTopChartData = getEvTopSellersChartData();
  const iceTopChartData = getIceTopSellersChartData();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0
    }}>
      <Box sx={{ 
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ px: 2, pt: 2, pb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ pb: 0 }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analytics overview and insights{selectedYear ? ` — ${selectedYear}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last Updated — EV: {lastUpdated.ev} | ICE: {lastUpdated.ice}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="dashboard-year-label">Year</InputLabel>
              <Select
                labelId="dashboard-year-label"
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadMasterExcel}
              disabled={excelDownloadLoading}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              {excelDownloadLoading ? 'Loading…' : 'Excel'}
            </Button>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ 
        px: 2,
        pt: 2,
        pb: 4,
      }}>
        {/* Key Stats Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <ShowChartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Key Insights
          </Typography>
          
          {loading.insights ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item}>
                  <Skeleton variant="rectangular" height={120} />
                </Grid>
              ))}
            </Grid>
          ) : (
            renderStatCards()
          )}
        </Box>
        
        {/* Analytics Charts Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <CompareArrowsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Market Analysis
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ChartContainer
                title="Top 10 EV Sellers (by brand)"
                isLoading={loading.insights}
                height="320px"
                chartRef={!loading.insights ? evChartRef : null}
                chartId="dashboard-ev-top-sellers"
                chartType="bar"
                sx={{ '& .MuiBox-root': { overflow: 'visible' } }}
              >
                {evTopSellers && evTopChartData && (
                  <EnhancedChart
                    ref={evChartRef}
                    type="bar"
                    horizontalBar
                    enhancedColors={false}
                    data={evTopChartData}
                    options={topSalesBarOptions}
                  />
                )}
              </ChartContainer>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <ChartContainer
                title="Top 10 ICE Sellers (by brand)"
                isLoading={loading.insights}
                height="320px"
                chartRef={!loading.insights ? iceChartRef : null}
                chartId="dashboard-ice-top-sellers"
                chartType="bar"
                sx={{ '& .MuiBox-root': { overflow: 'visible' } }}
              >
                {iceTopSellers && iceTopChartData && (
                  <EnhancedChart
                    ref={iceChartRef}
                    type="bar"
                    horizontalBar
                    enhancedColors={false}
                    data={iceTopChartData}
                    options={topSalesBarOptions}
                  />
                )}
              </ChartContainer>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <ChartContainer
                title="Market Share Trends"
                isLoading={loading.insights}
                height="280px"
                chartRef={!loading.insights ? trendsChartRef : null}
                chartId="dashboard-market-trends"
                chartType="line"
              >
                {marketTrends && (
                  <EnhancedChart
                    ref={trendsChartRef}
                    type="line"
                    data={getMarketTrendsChartData()}
                    colorPalette="combined"
                    options={marketTrendsChartOptions || {}}
                  />
                )}
              </ChartContainer>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <ChartContainer
                title="EV vs ICE - Yearly Comparison"
                isLoading={loading.insights}
                height="240px"
                chartRef={!loading.insights ? comparisonChartRef : null}
                chartId="dashboard-yearly-comparison"
                chartType="bar"
              >
                {marketTrends && (
                  <EnhancedChart
                    ref={comparisonChartRef}
                    type="bar"
                    data={getComparisonChartData()}
                    options={{
                      scales: {
                        x: {
                          stacked: true,
                        },
                        y: {
                          stacked: true,
                          max: 100
                        }
                      }
                    }}
                    colorPalette="combined"
                  />
                )}
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>
        
        {/* Geographic Distribution Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Geographic Distribution (EV Sales)
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ChartContainer
                title="Top 10 States (EV Sales)"
                isLoading={loading.insights}
                height="320px"
                chartRef={!loading.insights ? topStatesRef : null}
                chartId="dashboard-top-states"
                chartType="bar"
                sx={{ '& .MuiBox-root': { overflow: 'visible' } }}
              >
                {topStates.length > 0 && getTopStatesChartData() && (
                  <EnhancedChart
                    ref={topStatesRef}
                    type="bar"
                    horizontalBar
                    enhancedColors={false}
                    data={getTopStatesChartData()}
                    options={topSalesBarOptions}
                  />
                )}
              </ChartContainer>
            </Grid>

            <Grid item xs={12} sm={6}>
              <ChartContainer
                title="Top 10 Cities (EV Sales)"
                isLoading={loading.insights}
                height="320px"
                chartRef={!loading.insights ? topCitiesRef : null}
                chartId="dashboard-top-cities"
                chartType="bar"
                sx={{ '& .MuiBox-root': { overflow: 'visible' } }}
              >
                {topCities.length > 0 && getTopCitiesChartData() && (
                  <EnhancedChart
                    ref={topCitiesRef}
                    type="bar"
                    horizontalBar
                    enhancedColors={false}
                    data={getTopCitiesChartData()}
                    options={topSalesBarOptions}
                  />
                )}
              </ChartContainer>
            </Grid>

            <Grid item xs={12}>
              <ChartContainer
                title="Top 15 RTOs (EV Sales)"
                isLoading={loading.insights}
                height="400px"
                chartRef={!loading.insights ? topRtosRef : null}
                chartId="dashboard-top-rtos"
                chartType="bar"
                sx={{ '& .MuiBox-root': { overflow: 'visible' } }}
              >
                {topRtos.length > 0 && getTopRtosChartData() && (
                  <EnhancedChart
                    ref={topRtosRef}
                    type="bar"
                    horizontalBar
                    enhancedColors={false}
                    data={getTopRtosChartData()}
                    options={topSalesBarOptions}
                  />
                )}
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        {/* Pinned Charts Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              Your Pinned Charts
            </Typography>
            
            <Button 
              size="small" 
              variant="outlined" 
              color="primary"
              component="a"
              href="/custom-dashboard"
            >
              View Live Charts
            </Button>
          </Box>
          
          {loading.pinnedCharts ? (
            <Grid container spacing={3}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Card>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent>
                      <Skeleton width="80%" />
                      <Skeleton width="40%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : pinnedCharts.length === 0 ? (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 6,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No pinned charts yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pin charts from the analytics pages to see them here
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                component="a"
                href="/ev-analytics"
              >
                Go to Analytics
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {pinnedCharts.map((chart) => (
                <Grid item xs={12} sm={6} md={4} key={chart._id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                      }
                    }}
                  >
                    <CardHeader
                      title={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {chart.title}
                        </Typography>
                      }
                      subheader={new Date(chart.createdAt).toLocaleDateString()}
                      action={
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteChart(chart._id)}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{ pb: 0 }}
                    />
                    <CardContent sx={{ pt: 1, flexGrow: 1 }}>
                      <Chip 
                        label={chart.chartType} 
                        size="small" 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mb: 1
                        }} 
                      />
                      <CardMedia
                        component="img"
                        height={180}
                        image={chart.imageUrl}
                        alt={chart.title}
                        sx={{ borderRadius: 1, mt: 1 }}
                      />
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDownloadChart(chart)}
                        aria-label="download"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard; 